import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface DocumentationManual {
  id: string;
  module_type: 'crm' | 'charging';
  version: string;
  version_notes: string | null;
  is_current: boolean;
  created_by: string | null;
  created_at: string;
  published_at: string | null;
}

export interface DocumentationSection {
  id: string;
  manual_id: string;
  title: string;
  content: string;
  parent_section_id: string | null;
  display_order: number;
  icon: string | null;
  created_at: string;
  updated_at: string;
}

export function useDocumentation(moduleType: 'crm' | 'charging') {
  const queryClient = useQueryClient();

  // Fetch current manual
  const { data: currentManual, isLoading: manualsLoading } = useQuery({
    queryKey: ['documentation-manuals', moduleType],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('documentation_manuals')
        .select('*')
        .eq('module_type', moduleType)
        .eq('is_current', true)
        .maybeSingle();

      if (error) throw error;
      return data as DocumentationManual | null;
    },
  });

  // Fetch sections for current manual
  const { data: sections = [], isLoading: sectionsLoading } = useQuery({
    queryKey: ['documentation-sections', currentManual?.id],
    queryFn: async () => {
      if (!currentManual?.id) return [];

      const { data, error } = await supabase
        .from('documentation_sections')
        .select('*')
        .eq('manual_id', currentManual.id)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as DocumentationSection[];
    },
    enabled: !!currentManual?.id,
  });

  // Fetch all versions
  const { data: allVersions = [] } = useQuery({
    queryKey: ['documentation-versions', moduleType],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('documentation_manuals')
        .select('*')
        .eq('module_type', moduleType)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as DocumentationManual[];
    },
  });

  // Update section content
  const updateSectionMutation = useMutation({
    mutationFn: async ({ sectionId, content }: { sectionId: string; content: string }) => {
      const { error } = await supabase
        .from('documentation_sections')
        .update({ content, updated_at: new Date().toISOString() })
        .eq('id', sectionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation-sections'] });
      toast.success('Sectie bijgewerkt');
    },
    onError: (error) => {
      console.error('Error updating section:', error);
      toast.error('Fout bij bijwerken sectie');
    },
  });

  // Create new version
  const createVersionMutation = useMutation({
    mutationFn: async ({
      version,
      versionNotes,
      isMajor,
    }: {
      version: string;
      versionNotes: string;
      isMajor: boolean;
    }) => {
      // Create new manual
      const { data: newManual, error: manualError } = await supabase
        .from('documentation_manuals')
        .insert({
          module_type: moduleType,
          version,
          version_notes: versionNotes,
          is_current: false,
        })
        .select()
        .single();

      if (manualError) throw manualError;

      // Copy sections from current manual
      if (currentManual?.id && sections.length > 0) {
        const newSections = sections.map((section) => ({
          manual_id: newManual.id,
          title: section.title,
          content: section.content,
          parent_section_id: section.parent_section_id,
          display_order: section.display_order,
          icon: section.icon,
        }));

        const { error: sectionsError } = await supabase
          .from('documentation_sections')
          .insert(newSections);

        if (sectionsError) throw sectionsError;
      }

      return newManual;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation-manuals'] });
      queryClient.invalidateQueries({ queryKey: ['documentation-versions'] });
      toast.success('Nieuwe versie aangemaakt');
    },
    onError: (error) => {
      console.error('Error creating version:', error);
      toast.error('Fout bij aanmaken nieuwe versie');
    },
  });

  // Publish version (set as current)
  const publishVersionMutation = useMutation({
    mutationFn: async (manualId: string) => {
      // Set all versions to not current
      const { error: resetError } = await supabase
        .from('documentation_manuals')
        .update({ is_current: false })
        .eq('module_type', moduleType);

      if (resetError) throw resetError;

      // Set specified version as current
      const { error: publishError } = await supabase
        .from('documentation_manuals')
        .update({ is_current: true, published_at: new Date().toISOString() })
        .eq('id', manualId);

      if (publishError) throw publishError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation-manuals'] });
      queryClient.invalidateQueries({ queryKey: ['documentation-versions'] });
      toast.success('Versie gepubliceerd');
    },
    onError: (error) => {
      console.error('Error publishing version:', error);
      toast.error('Fout bij publiceren versie');
    },
  });

  // Initialize manual with starter content
  const initializeManualMutation = useMutation({
    mutationFn: async () => {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('Not authenticated');

      // Create the manual
      const { data: manual, error: manualError } = await supabase
        .from('documentation_manuals')
        .insert({
          module_type: moduleType,
          version: '1.0',
          version_notes: 'Initiële versie van de handleiding',
          is_current: true,
          published_at: new Date().toISOString(),
          created_by: user.id,
        })
        .select()
        .single();

      if (manualError) throw manualError;

      // Define sections based on module type
      const crmSections = [
        { title: 'Dashboard', icon: 'LayoutDashboard', content: '# Dashboard\n\nHet dashboard biedt een overzicht van:\n- KPI\'s en statistieken\n- Recente activiteiten\n- Snelkoppelingen naar belangrijke modules\n\nNavigeer via het zijmenu naar andere modules.' },
        { title: 'Notificaties', icon: 'Bell', content: '# Notificaties\n\nBeheer je meldingen:\n- Nieuwe leads en projectupdates\n- Taak herinneringen\n- Systeem berichten\n\nKlik op het belletje in de header om notificaties te bekijken.' },
        { title: 'Agenda', icon: 'Calendar', content: '# Agenda\n\nAgenda functionaliteit:\n- Outlook kalender koppeling\n- Afspraken beheren\n- Synchronisatie met CRM projecten\n\nKoppel je Outlook account via Instellingen.' },
        { title: 'Leads', icon: 'Users', content: '# Leads\n\nBeheer potentiële klanten:\n- Nieuwe leads aanmaken\n- Kanban workflow (nieuw → contact → offerte → gewonnen/verloren)\n- Lead status bijwerken\n- Notities en activiteiten\n\nSleep leads tussen kolommen om status te wijzigen.' },
        { title: 'Organisaties', icon: 'Building', content: '# Organisaties\n\nBedrijven beheren:\n- Organisatie gegevens\n- Gekoppelde contactpersonen\n- SharePoint integratie\n- Projecten per organisatie\n\nKlik op een organisatie voor detail weergave.' },
        { title: 'Contacten', icon: 'UserCircle', content: '# Contacten\n\nContactpersonen beheren:\n- Persoonlijke gegevens\n- Gekoppeld aan organisaties\n- Communicatie geschiedenis\n- GDPR compliance\n\nVoeg nieuwe contacten toe via de + knop.' },
        { title: 'Projecten', icon: 'FolderKanban', content: '# Projecten\n\nProject management:\n- Workflow stages\n- Inspecties koppelen\n- To-do\'s per project\n- Documenten en businesscases\n- SharePoint integratie\n\nBeheer alle project aspecten op de detail pagina.' },
        { title: 'Offertes', icon: 'FileText', content: '# Offertes\n\nOfferte beheer:\n- Templates aanmaken\n- PDF designer met placeholders\n- Versie beheer\n- Automatische PDF generatie\n\nGebruik de PDF designer om professionele offertes te maken.' },
        { title: 'Businesscases', icon: 'Calculator', content: '# Businesscases\n\nFinanciële analyses:\n- ROI berekeningen\n- Scenario vergelijkingen\n- Cashflow projecties\n- Subsidie mogelijkheden\n\nKoppel businesscases aan projecten voor financiële inzichten.' },
        { title: 'Flex-E', icon: 'Zap', content: '# Flex-E\n\nEnergie management simulaties:\n- Machine library\n- Scenario\'s bouwen\n- Simulaties uitvoeren\n- Batterij dimensionering\n\nUpload energiedata en test verschillende scenario\'s.' },
        { title: 'Battery Planner', icon: 'Battery', content: '# Battery Planner\n\nBatterij voorraadbeheer:\n- Containers traceren\n- Batterij allocaties\n- Reserveringen\n- Status overzicht\n\nBeheer de volledige batterij supply chain.' },
        { title: 'To-do\'s', icon: 'CheckSquare', content: '# To-do\'s\n\nTaken beheer:\n- Persoonlijke taken\n- Team taken (gedeeld)\n- Project gekoppelde taken\n- Locatie gekoppelde taken\n- Afhankelijkheden\n\nFilter op status, prioriteit en deadline.' },
        { title: 'Instellingen', icon: 'Settings', content: '# Instellingen\n\nSysteem configuratie:\n- Profiel instellingen\n- Outlook koppeling\n- SharePoint koppeling\n- Module beheer (super admin)\n\nPas je voorkeuren aan in dit overzicht.' },
      ];

      const chargingSections = [
        { title: 'Dashboard', icon: 'LayoutDashboard', content: '# Dashboard\n\nDashboard overzicht:\n- Interactieve kaart met alle locaties\n- Filter op stages en tags\n- Stage overzicht per categorie\n- Marker grootte aanpasbaar\n\nGebruik de filters om specifieke locaties te vinden.' },
        { title: 'Acquisitie', icon: 'Target', content: '# Acquisitie Pipeline\n\n5-stage kanban workflow:\n1. **Nieuw** - Intake locaties (grid layout)\n2. **Evaluatie** - Eerste beoordeling\n3. **Uitwerking** - Verdere uitwerking\n4. **Onderhandeling** - Contract besprekingen\n5. **Contracting** - Finale fase\n\nSleep locaties tussen stages om voortgang bij te werken.\nGebruik tags (bijv. SPULA) voor categorisering.' },
        { title: 'Realisatie', icon: 'Hammer', content: '# Realisatie\n\nBouw voortgang stages:\n- Voorlopig ontwerp\n- Definitief ontwerp\n- Bouwvoorbereiding\n- Bouwuitvoering\n\nVolg de bouw van laadpleinen in deze module.' },
        { title: 'Operatie', icon: 'Activity', content: '# Operatie\n\nActieve locaties:\n- Operationele laadpleinen\n- Onderhoudsgeschiedenis\n- Performance monitoring\n\nBeheer live locaties hier.' },
        { title: 'Archief', icon: 'Archive', content: '# Archief (Verloren)\n\nVerloren locaties:\n- Niet doorgegane projecten\n- Reden van verlies\n- Lessons learned\n\nBewaar historische data voor analyse.' },
        { title: 'Locatie Detail', icon: 'MapPin', content: '# Locatie Detail Pagina\n\nVolledig locatie overzicht:\n- **Overzicht**: Locatie info + mini kaart\n- **Projectmanagement**: Timeline, notities, to-do\'s\n- **Businesscase**: Project documenten\n- **Documenten**: Algemene bestanden\n- **Activiteiten**: Audit log\n\nAlles wat je nodig hebt per locatie op één plek.' },
        { title: 'Export', icon: 'Download', content: '# Export\n\nKaart export functionaliteit:\n- Selecteer specifieke locaties per stage\n- Privacy mode (verberg exacte adressen)\n- Configureer marker labels\n- Competitor en wensen overlay\n- Export naar PNG\n\nCreëer presentatie-klare kaarten voor rapportages.' },
        { title: 'Concurrentie', icon: 'Swords', content: '# Concurrentie\n\nConcurrent tracking:\n- LEAP24 locaties (automatisch)\n- Milence netwerk\n- Overige concurrenten\n- Kaart overlay\n\nVisualiseer marktpositionering ten opzichte van concurrenten.' },
        { title: 'Wensen', icon: 'Heart', content: '# Wensen\n\nKlant wensen module:\n- Transporteurs met gewenste locaties\n- Prioriteit en timeline\n- Contact informatie\n- Kaart overlay (groene markers)\n\nTraceer klant vraag voor strategische planning.' },
        { title: 'To-do\'s', icon: 'CheckSquare', content: '# To-do\'s\n\nLocatie-gebonden taken:\n- Toegewezen aan jou\n- Deadline tracking\n- Status beheer\n- Gekoppeld aan specifieke locaties\n\nBeheer je werkzaamheden per laadplein.' },
      ];

      const sectionsToInsert = moduleType === 'crm' ? crmSections : chargingSections;

      const sectionsWithOrder = sectionsToInsert.map((section, index) => ({
        ...section,
        manual_id: manual.id,
        display_order: index + 1,
      }));

      const { error: sectionsError } = await supabase
        .from('documentation_sections')
        .insert(sectionsWithOrder);

      if (sectionsError) throw sectionsError;

      return manual;
    },
    onSuccess: () => {
      toast.success('Handleiding geïnitialiseerd!');
      queryClient.invalidateQueries({ queryKey: ['documentation-manuals', moduleType] });
      queryClient.invalidateQueries({ queryKey: ['documentation-sections'] });
      queryClient.invalidateQueries({ queryKey: ['documentation-versions', moduleType] });
    },
    onError: (error) => {
      console.error('Initialize manual error:', error);
      toast.error('Fout bij initialiseren handleiding');
    },
  });

  // Get batch content for upgrades
  const getBatchContent = (batchNumber: number) => {
    const batches: Record<number, Record<string, string>> = {
      1: {
        'Dashboard': `## Introductie

Het CRM Dashboard is het centrale overzicht van uw customer relationship management systeem. Het biedt real-time inzicht in bedrijfsprestaties via KPI's, recente activiteiten, en snelkoppelingen naar belangrijke modules. Het dashboard is ontworpen voor maximale efficiëntie, zodat u direct ziet waar actie nodig is.

## Navigatie

Na inloggen landt u automatisch op het Dashboard. U kunt altijd terug door op het Greenvision logo linksboven te klikken, of "Dashboard" in het zijmenu te selecteren. Op mobiele apparaten opent u het menu via de hamburger icoon.

## Functies Overzicht

- **KPI Widgets**: Live statistieken (aantal leads, conversie ratio, open projecten, omzet)
- **Activiteiten Feed**: Recente wijzigingen in leads, projecten en offertes
- **Snelle Acties**: Direct nieuwe leads of projecten aanmaken zonder navigatie
- **Filtermogelijkheden**: Toon alleen relevante data voor uw workflow
- **Responsive Design**: Optimaal op desktop, tablet en mobiel

## Stap-voor-stap Instructies

1. **Dashboard Bekijken**: Open CRM systeem → automatisch op dashboard
2. **KPI's Interpreteren**: Klik op een KPI widget voor details en drill-down naar onderliggende data
3. **Recente Activiteiten**: Scroll door de feed om wijzigingen te volgen
4. **Snelle Actie Uitvoeren**: Klik op een "+" knop in widgets om direct items aan te maken
5. **Navigeer naar Module**: Gebruik zijmenu of klik op widget om naar specifieke module te gaan

## 💡 Tips & Best Practices

- Bekijk het dashboard dagelijks aan het begin van de dag voor overzicht
- Gebruik de activiteiten feed om wijzigingen van collega's te monitoren
- Pin belangrijke KPI's bovenaan voor snelle toegang
- Gebruik snelle acties om tijd te besparen bij het aanmaken van records
- Koppel het dashboard aan een second screen voor real-time monitoring

## Veelgestelde Vragen

**Q: Waarom zie ik niet alle KPI's?**
A: Uw gebruikersrol bepaalt welke data u ziet. Neem contact op met uw admin voor extra toegang.

**Q: Hoe vaak verversen KPI's?**
A: Real-time bij wijzigingen. Bij twijfel, ververs de pagina (F5).

**Q: Kan ik het dashboard aanpassen?**
A: Super admins kunnen via Instellingen widgets in/uitschakelen per rol.`,

        'Leads': `## Introductie

De Leads module is het startpunt van uw verkoopproces. Hier beheert u potentiële klanten vanaf eerste contact tot conversie naar project of verlies. Het kanban board visualiseert uw sales pipeline en maakt statusupdates intuïtief via drag-and-drop.

## Navigatie

Ga naar **Leads** in het zijmenu (gebruikers icoon met meerdere personen). De pagina opent met een kanban board met kolommen: Nieuw, Contact Opgenomen, Offerte Verstuurd, Gewonnen, Verloren.

## Functies Overzicht

- **Kanban Workflow**: Visuele pipeline met drag-and-drop statusupdates
- **Lead Scoring**: Automatische prioritering op basis van potentieel
- **Contact Tracking**: Alle communicatie en notities per lead
- **Automatische Conversie**: Transform lead naar project bij "Gewonnen" status
- **Filter & Zoek**: Vind leads snel op naam, organisatie, status of score
- **Bulk Acties**: Selecteer meerdere leads voor batch updates

## Stap-voor-stap Instructies

1. **Nieuwe Lead Aanmaken**: Klik op "+ Nieuwe Lead" knop rechtsboven → vul gegevens in (naam, organisatie, bron, notities) → opslaan
2. **Lead Bewerken**: Klik op lead card → wijzig gegevens in modal → opslaan
3. **Status Wijzigen**: Sleep lead card naar gewenste kolom (bijv. van Nieuw naar Contact Opgenomen)
4. **Notitie Toevoegen**: Open lead detail → tab "Notities" → typ notitie → opslaan
5. **Lead Converteren**: Sleep naar "Gewonnen" kolom → systeem vraagt of project moet worden aangemaakt → bevestig

## 💡 Tips & Best Practices

- Update leads dagelijks om pipeline actueel te houden
- Voeg altijd notities toe na klantcontact voor historische context
- Gebruik lead scoring om prioriteiten te stellen (focus op hoge scores eerst)
- Stel herinneringen in voor follow-up via next action date veld
- Converteer verloren leads niet direct: bewaar voor toekomstige analyse
- Tag leads met labels (bijv. "SPULA", "urgent") voor snelle filtering

## Veelgestelde Vragen

**Q: Wat gebeurt er met lead data na conversie naar project?**
A: De lead blijft zichtbaar in "Gewonnen" kolom en linkt automatisch naar het nieuwe project. Historische notities blijven bewaard.

**Q: Kan ik een lead terug verplaatsen naar een eerdere status?**
A: Ja, sleep de card gewoon terug naar de gewenste kolom.

**Q: Hoe wordt lead score berekend?**
A: Op basis van bedrijfsgrootte, budget indicatie en urgentie. Admins kunnen de formule aanpassen in settings.`,

        'Organisaties': `## Introductie

De Organisaties module is uw centrale database voor bedrijfsrelaties. Hier beheert u alle informatie over klanten, prospects en partners, inclusief contactpersonen, projecten en SharePoint documentatie. Een gestructureerde organisatiedatabase is essentieel voor effectief CRM.

## Navigatie

Selecteer **Organisaties** in het zijmenu (gebouw icoon). De pagina toont een lijst van alle bedrijven met zoek- en filtermogelijkheden.

## Functies Overzicht

- **Bedrijfsprofiel**: Naam, adres, KvK nummer, website, branche
- **Contactpersonen Koppeling**: Alle medewerkers binnen een organisatie
- **Project Overzicht**: Alle lopende en afgesloten projecten
- **SharePoint Integratie**: Automatische folder structuur per organisatie
- **Status Tracking**: Prospect, Actieve Klant, Inactief, Partner
- **Company Enrichment**: Automatische data aanvulling via externe bronnen

## Stap-voor-stap Instructies

1. **Organisatie Aanmaken**: Klik "+ Nieuwe Organisatie" → vul minimaal naam in → opslaan (andere velden optioneel)
2. **Gegevens Bewerken**: Klik op organisatie in lijst → wijzig velden → opslaan
3. **Contactpersoon Toevoegen**: Open organisatie detail → tab "Contacten" → "+ Nieuw Contact" → vul gegevens in → opslaan
4. **SharePoint Koppelen**: Open organisatie → tab "Documenten" → klik "SharePoint Koppeling Activeren" → autoriseer → folders worden automatisch aangemaakt
5. **Project Koppelen**: Bij project aanmaken, selecteer organisatie in dropdown → project verschijnt automatisch in organisatie detail

## 💡 Tips & Best Practices

- Gebruik KvK nummer voor unieke identificatie en automatische data enrichment
- Koppel altijd minimaal één contactpersoon voor communicatie
- Activeer SharePoint direct bij nieuwe klanten voor gestructureerde documentopslag
- Tag organisaties met industrie labels voor segment analyse
- Update status regelmatig (prospect → actieve klant bij eerste order)
- Gebruik notities tab voor belangrijke achtergrond info (bijv. besluitvormingsproces)

## Veelgestelde Vragen

**Q: Wat is het verschil tussen Organisatie en Contact?**
A: Organisatie = bedrijf/entiteit. Contact = individuele persoon binnen die organisatie.

**Q: Kan één contact bij meerdere organisaties horen?**
A: Nee, elk contact hoort bij één organisatie. Voor personen bij meerdere bedrijven maakt u aparte contact records.

**Q: Hoe werkt automatische data enrichment?**
A: Bij invoer van KvK nummer haalt het systeem publieke bedrijfsdata op (adres, branche, grootte). Handmatige correctie blijft mogelijk.`,

        'Projecten': `## Introductie

De Projecten module is het hart van uw operationele CRM. Hier beheert u de volledige lifecycle van projecten: van initiatief via realisatie tot oplevering. Elk project bevat alle relevante informatie, documenten, inspecties en taken op één centrale plek.

## Navigatie

Ga naar **Projecten** in het zijmenu (folder kanban icoon). De pagina toont een overzicht van alle projecten met status filters en zoekfunctie.

## Functies Overzicht

- **Project Lifecycle**: Stages van Initiatief → Ontwerp → Realisatie → Operatie
- **Document Management**: Upload bestanden en koppel SharePoint folders
- **Inspectie Tracking**: Koppel inspecties aan projecten met foto's en rapporten
- **To-do Management**: Takenlijsten per project met toewijzing aan teamleden
- **Businesscase Koppeling**: Link financiële analyses aan projecten
- **Flex-E Simulaties**: Energiedata en batterij dimensionering per project
- **Stakeholder Beheer**: Organisatie, contactpersonen en externe partijen

## Stap-voor-stap Instructies

1. **Project Aanmaken**: Klik "+ Nieuw Project" → vul projectnaam, organisatie en startdatum in → selecteer stage → opslaan
2. **Stage Wijzigen**: Open project → wijzig stage dropdown → opslaan (automatische notificatie naar teamleden)
3. **Document Uploaden**: Project detail → tab "Documenten" → drag-and-drop bestand of klik "Upload" → selecteer categorie → opslaan
4. **Inspectie Toevoegen**: Tab "Inspecties" → "+ Nieuwe Inspectie" → vul datum en type in → wijs inspector toe → opslaan
5. **To-do Aanmaken**: Tab "Taken" → "+ Nieuwe Taak" → titel, deadline en toegewezen persoon → opslaan
6. **Businesscase Koppelen**: Tab "Businesscase" → "+ Nieuwe Businesscase" → vul financiële gegevens → opslaan

## 💡 Tips & Best Practices

- Update project stages wekelijks om realtime overzicht te behouden
- Gebruik SharePoint voor grote bestanden (CAD, video) en uploads voor snelle documenten
- Wijs taken altijd toe aan specifieke personen met realistische deadlines
- Maak een inspectie template voor standaard controles (herbruikbaar)
- Link Flex-E simulaties direct aan project voor energiedata historiek
- Gebruik notities tab voor belangrijke beslissingen en meeting notes
- Stel automatische herinneringen in voor kritische milestones

## Veelgestelde Vragen

**Q: Kan ik een project archiveren zonder te verwijderen?**
A: Ja, wijzig stage naar "Archief". Het project verdwijnt uit actieve lijst maar blijft doorzoekbaar.

**Q: Hoe werkt automatische SharePoint folder aanmaken?**
A: Bij eerste document upload naar project met gekoppelde organisatie wordt automatisch een folder structuur aangemaakt in SharePoint.

**Q: Kunnen meerdere gebruikers tegelijk een project bewerken?**
A: Ja, het systeem ondersteunt realtime updates. Let op: laatste opslag overschrijft eerdere wijzigingen in hetzelfde veld.`
      },
      2: {
        'Dashboard': `## Introductie

Het Charging Dashboard is uw command center voor laadplein ontwikkeling en beheer. Het combineert een interactieve kaart van Nederland met alle locaties, krachtige filtermogelijkheden en real-time stage overzichten. Dit dashboard geeft u direct inzicht in uw volledige portfolio van acquisitie tot operatie.

## Navigatie

**CRM Gebruikers**: Klik "Charging" in zijmenu → selecteer "Dashboard"
**Charging Gebruikers**: Automatisch na login op Charging portal (of klik "Dashboard")

Het dashboard opent met een grote interactieve kaart en filter controls eronder.

## Functies Overzicht

- **Interactieve Kaart**: Alle locaties op kaart van Nederland met kleuren per stage
- **Stage Filtering**: Toon alleen Acquisitie, Realisatie, Operatie of Verloren locaties
- **Tag Filtering**: Filter op locatie tags (bijv. "SPULA" voor specifieke projecten)
- **Marker Grootte Slider**: Pas marker size aan voor optimale visualisatie
- **Stage Overzicht Cards**: Totaal aantal locaties per stage categorie
- **Kaart Controls**: Zoom, satelliet view toggle, layer controls
- **Click-through**: Klik op marker voor snelle toegang naar locatie detail

## Stap-voor-stap Instructies

1. **Dashboard Openen**: Navigeer naar Charging → Dashboard
2. **Locaties Bekijken op Kaart**: Markers tonen alle locaties, kleuren = stages (blauw=Acquisitie, oranje=Realisatie, groen=Operatie, grijs=Verloren)
3. **Filteren op Stage**: Klik stage filter dropdown → selecteer gewenste stage(s) → kaart toont alleen die locaties
4. **Tag Filter Gebruiken**: Open tag dropdown → selecteer tag (bijv. "SPULA") → alleen getagde locaties verschijnen
5. **Marker Grootte Aanpassen**: Sleep marker size slider om markers groter/kleiner te maken (15-50px)
6. **Satelliet View**: Klik layer control rechtsboven → selecteer "Satelliet" voor luchtfoto's
7. **Locatie Detail Openen**: Klik op marker → popup met naam/adres → klik naam voor detail pagina

## 💡 Tips & Best Practices

- Start elke dag met dashboard bekijken voor overzicht van alle actieve locaties
- Gebruik tag filtering om specifieke klant portfolios te isoleren (bijv. alle SPULA locaties)
- Pas marker grootte aan bij veel overlappende locaties in dichte gebieden
- Satelliet view is handig bij site bezoek voorbereiding (zie omgeving)
- Combineer stage + tag filters voor gerichte analyses (bijv. "Acquisitie" + "SPULA")
- Gebruik stage overzicht cards voor quick KPI check (aantal locaties per fase)
- Bookmark veelgebruikte filter combinaties via browser bookmarks

## Veelgestelde Vragen

**Q: Waarom zijn sommige markers grijs?**
A: Grijze markers zijn verloren/gearchiveerde locaties. Filter deze uit via stage dropdown.

**Q: Hoe weet ik welke kleur bij welke stage hoort?**
A: Blauw=Acquisitie, Oranje=Realisatie, Groen=Operatie, Grijs=Verloren. Dit is consistent door hele systeem.

**Q: Kan ik de kaart exporteren voor presentaties?**
A: Ja, ga naar "Export" module voor PNG export functionaliteit met configureerbare markers.`,

        'Acquisitie': `## Introductie

De Acquisitie module is uw sales pipeline voor nieuwe laadplein locaties. Het 5-stage kanban board visualiseert de volledige flow van eerste prospect tot getekend contract. Met drag-and-drop, LOCODE nummering en tag systeem beheert u tientallen locaties efficiënt.

## Navigatie

Klik **Charging** in zijmenu → **Acquisitie**. De pagina opent met een kanban board met 5 stages.

## Functies Overzicht

- **5-Stage Pipeline**:
  1. **Nieuw**: Intake, grid layout (4 kolommen desktop)
  2. **Evaluatie**: Eerste beoordeling haalbaarheid
  3. **Uitwerking**: Verdere ontwikkeling en analyse
  4. **Onderhandeling**: Contract besprekingen
  5. **Contracting**: Finale fase tot handtekening
- **Drag-and-Drop**: Sleep locaties tussen stages voor status updates
- **LOCODE Nummering**: Automatische UN/LOCODE codes (bijv. OIW01 voor Oisterwijk)
- **Tag Systeem**: Label locaties (bijv. "SPULA") voor categorisering
- **Real-time Updates**: Wijzigingen direct zichtbaar voor alle gebruikers
- **Search & Filter**: Zoek op naam/stad, filter op tags

## Stap-voor-stap Instructies

1. **Nieuwe Locatie Toevoegen**: Klik "+ Nieuwe Locatie" → vul adres en stad (verplicht) → optioneel: beschikbaar vermogen, oppervlakte → opslaan → verschijnt in "Nieuw" stage
2. **Stage Wijzigen via Drag**: Klik en houd locatie card vast → sleep naar gewenste stage kolom → loslaten → automatisch opgeslagen
3. **Tag Toevoegen**: Open locatie card → klik "Bewerken" → veld "Tags" → typ tag naam (bijv. "SPULA") → klik "Toevoegen" of selecteer bestaande tag → opslaan
4. **LOCODE Bekijken**: Elke locatie krijgt automatisch LOCODE bij aanmaken (zichtbaar op card)
5. **Notities Toevoegen**: Open locatie detail → tab "Notities" → typ notitie → opslaan
6. **Timeline Update**: Locatie detail → tab "Projectmanagement" → "+ Milestone" → vul datum en beschrijving → opslaan

## 💡 Tips & Best Practices

- Gebruik "Nieuw" stage als dagelijkse intake queue: process nieuwe leads snel door naar Evaluatie
- Evaluatie stage = eerste go/no-go check: filter verliezers snel uit
- Uitwerking fase: koppel Flex-E simulaties en eerste businesscases
- Tag consistent: gebruik vaste tags per klant/project type voor filtering
- LOCODE systeem maakt locaties uniek identificeerbaar in communicatie
- Gebruik notities voor belangrijke klant afspraken en site observations
- Stel milestones in timeline voor kritische deadlines (bijv. "Contract deadline 15/01")
- Drag-and-drop werkt horizontaal: sleep direct naar rechts/links voor snelle updates

## Veelgestelde Vragen

**Q: Wat gebeurt er als ik locatie naar Contracting verplaats?**
A: Locatie blijft in Acquisitie zichtbaar tot handtekening. Bij definitieve "Go" verplaatst u naar Realisatie module.

**Q: Kan ik een locatie terug naar eerdere stage verplaatsen?**
A: Ja, volledige bi-directionele workflow. Sleep terug als situatie wijzigt.

**Q: Hoe werkt LOCODE nummering bij meerdere locaties in zelfde stad?**
A: Automatische incrementing: eerste Oisterwijk = OIW01, tweede = OIW02, etc.`,

        'Locatie Detail': `## Introductie

De Locatie Detail pagina is het centrale dossier per laadplein. Hier vindt u alle informatie, documenten, taken en historiek op één plek. Met 5 hoofdtabs beheert u de volledige locatie lifecycle van eerste prospect tot operationeel laadplein.

## Navigatie

**Vanuit Dashboard**: Klik marker op kaart → popup → klik locatie naam
**Vanuit Kanban**: Klik locatie card in Acquisitie/Realisatie/Operatie
**Directe URL**: /charging/locatie/:id of /crm/charging/locatie/:id (CRM users)

## Functies Overzicht

**Tab 1: Overzicht**
- Locatie informatie (adres, vermogen, oppervlakte, status, tags)
- Mini kaart met single marker
- Projectmanagement sectie: Timeline, Notities, To-do's

**Tab 2: Businesscase**
- Upload project documenten (contracten, ontwerpen)
- PDF preview functionaliteit
- Bestandsbeheer met delete optie

**Tab 3: Documenten**
- Algemene document uploads
- Categorisering (contract, vergunning, tekening)
- Drag-and-drop upload

**Tab 4: Activiteiten**
- Automatische audit log (read-only)
- Alle wijzigingen getrackt (wie, wanneer, wat)

## Stap-voor-stap Instructies

1. **Locatie Info Bekijken/Bewerken**: Tab "Overzicht" → klik "Bewerken" button → wijzig velden → opslaan
2. **Milestone Toevoegen**: Scroll naar "Timeline" sectie → "+ Milestone" → datum, titel, beschrijving → opslaan
3. **Notitie Toevoegen**: Sectie "Notities" → typ notitie in tekstveld → klik "Toevoegen"
4. **To-do Aanmaken**: Sectie "To-do's" → "+ Taak" → titel, deadline, toegewezen persoon → opslaan
5. **Businesscase Document Uploaden**: Tab "Businesscase" → drag-and-drop PDF of klik "Upload" → bestand wordt opgeslagen
6. **PDF Bekijken**: Klik op document naam → preview modal opent → navigeer door pagina's
7. **Document Categoriseren**: Tab "Documenten" → upload bestand → selecteer type (contract/vergunning/tekening) → opslaan
8. **Activiteiten Bekijken**: Tab "Activiteiten" → scroll door chronologische log van wijzigingen

## 💡 Tips & Best Practices

- Update timeline milestones bij elke belangrijke stap (vergunning aangevraagd, contract getekend)
- Gebruik notities voor korte updates, documenten voor officiële bestanden
- Tag to-do's met deadlines voor kritische acties (gebruik next action date)
- Businesscase tab = alleen project-kritische docs, Documenten tab = rest
- Check activiteiten log bij onduidelijkheden over wie wat wijzigde
- Mini kaart is handig voor quick location check tijdens telefoon gesprekken
- Edit locatie info direct op overzicht tab zonder extra modal te openen

## Veelgestelde Vragen

**Q: Wat is verschil tussen Businesscase en Documenten tab?**
A: Businesscase = project documenten (contracten, ontwerpen). Documenten = algemene bestanden met categorisering.

**Q: Wie kan notities/milestones bewerken?**
A: De creator of admin/super_admin. Anderen kunnen alleen lezen.

**Q: Worden activiteiten automatisch gelogd?**
A: Ja, database triggers loggen alle wijzigingen automatisch. Geen handmatige input mogelijk.`,

        'Export': `## Introductie

De Export module stelt u in staat professionele kaarten te genereren voor presentaties en rapporten. U controleert welke locaties, markers en labels verschijnen en exporteert naar PNG. Met privacy mode kunt u exacte adressen verbergen voor publieke presentaties.

## Navigatie

Ga naar **Charging** → **Export** in het zijmenu. De pagina opent met een grote kaart en uitgebreide filter controls eronder.

## Functies Overzicht

- **Granulaire Locatie Selectie**: Selecteer individuele locaties per stage via checkboxes
- **Stage Filtering**: Toon/verberg volledige stages (Acquisitie, Realisatie, Operatie, Verloren)
- **Privacy Mode**: Verberg exacte adressen, toon alleen stad centers
- **Marker Label Configuratie**: Kies welke info in popups (naam, stad, stage, adres, vermogen, chargers)
- **Competitor Overlay**: Toon concurrenten (rood) en wensen (groen) op zelfde kaart
- **Marker Grootte**: Aanpasbare marker size voor optimale presentatie
- **PNG Export**: Download kaart als afbeelding voor PowerPoint/rapporten
- **Satelliet/Street View Toggle**: Kies beste achtergrond voor presentatie

## Stap-voor-stap Instructies

1. **Locaties Selecteren**: Expand stage (bijv. "Acquisitie") → vink individuele locaties aan/uit → alleen aangevinkte verschijnen op kaart
2. **Bulk Selectie**: Klik master checkbox bij stage naam → selecteert/deselecteert alle locaties in die stage
3. **Privacy Mode Activeren**: Toggle "Verberg exacte locaties" aan → markers verplaatsen naar stad centers (database blijft ongewijzigd)
4. **Marker Labels Configureren**: Vink gewenste info aan (naam, stad, stage, adres, vermogen, chargers) → popup content update direct
5. **Competitor Overlay**: Toggle "Concurrentie" aan → rode markers verschijnen voor concurrent sites
6. **Wensen Overlay**: Toggle "Wensen" aan → groene markers voor gewenste klant locaties
7. **Marker Grootte Aanpassen**: Gebruik slider om markers groter/kleiner te maken voor optimale visualisatie
8. **Kaart Exporteren**: Klik "Exporteer naar PNG" → kaart wordt gedownload als afbeelding

## 💡 Tips & Best Practices

- Voor publieke presentaties: privacy mode ON + alleen naam/stad labels
- Voor interne rapporten: volledige adressen en vermogen info
- Competitor overlay is krachtig voor markt positionering visualisatie
- Gebruik wensen overlay (groen) om klant vraag te tonen aan stakeholders
- Satelliet view werkt goed voor site context (omgeving, bereikbaarheid)
- Grotere markers voor presentaties op beamers (50px), kleine voor gedrukte rapporten (20px)
- Test verschillende combinaties voor beste visualisatie (bijv. alleen Operatie + Competitors)
- Export multiple versies voor verschillende doelgroepen (intern vs extern)

## Veelgestelde Vragen

**Q: Blijven exacte locaties verborgen na export?**
A: Nee, privacy mode wijzigt database niet. Het is alleen voor de export. Na export zijn originele coördinaten weer zichtbaar.

**Q: Kan ik meerdere exports tegelijk downloaden?**
A: Nee, maar u kunt snel verschillende configuraties exporteren door filters te wijzigen en opnieuw te exporteren.

**Q: Waarom zie ik sommige markers niet na selectie?**
A: Check of de kaart ver genoeg uitgezoomd is. Sommige markers kunnen buiten viewport vallen.`
      },
      3: {
        'Notificaties': `## Introductie

De Notificaties module houdt u op de hoogte van belangrijke updates in het CRM systeem. Van nieuwe leads en project statuswijzigingen tot taakherinneringen - u mist nooit kritische events. Het systeem stuurt real-time notificaties naar uw inbox binnen de applicatie.

## Navigatie

Klik op het **belletje icoon** in de header (rechtsboven). Een dropdown toont recente notificaties. Voor volledig overzicht: klik "Alle notificaties" of ga naar Notificaties via zijmenu.

## Functies Overzicht

- **Real-time Alerts**: Instant notificaties bij belangrijke events
- **Categorieën**: Lead updates, project wijzigingen, taken, systeem meldingen
- **Read/Unread Status**: Markeer gelezen/ongelezen voor overzicht
- **Click-through**: Direct naar gerelateerde record (lead, project, taak)
- **Filter Opties**: Toon alleen bepaalde categorieën
- **Notificatie Centrum**: Volledig overzicht van alle historische meldingen

## Stap-voor-stap Instructies

1. **Notificaties Bekijken**: Klik belletje icoon → dropdown toont laatste 5 notificaties
2. **Notificatie Openen**: Klik op notificatie → navigeert naar gerelateerde record (bijv. lead detail)
3. **Markeer als Gelezen**: Klik op notificatie (automatisch gelezen) of swipe → "Markeer als gelezen"
4. **Alle Notificaties**: Klik "Alle notificaties" in dropdown → volledige pagina met historiek
5. **Filteren**: Notificaties pagina → filter dropdown → selecteer categorie (leads/projecten/taken)
6. **Oude Notificaties Verwijderen**: Selecteer notificaties → bulk actie "Verwijderen"

## 💡 Tips & Best Practices

- Check notificaties minimaal 2x per dag (ochtend + middag)
- Markeer belangrijke notificaties als ongelezen als reminder voor later
- Gebruik click-through voor snelle navigatie naar relevante records
- Filter op categorie bij specifieke workflow (bijv. alleen project updates)
- Archiveer oude notificaties wekelijks om inbox clean te houden
- Stel browser notificaties in voor real-time alerts (vraag admin)

## Veelgestelde Vragen

**Q: Krijg ik notificaties voor alles in het systeem?**
A: Nee, alleen voor records waar u eigenaar van bent of aan toegewezen bent, plus systeem-brede alerts.

**Q: Kan ik email notificaties ontvangen?**
A: Afhankelijk van admin settings. Vraag uw admin om email forwarding te activeren.

**Q: Hoelang blijven notificaties bewaard?**
A: 90 dagen. Daarna automatische cleanup van oude notificaties.`,

        'Agenda': `## Introductie

De Agenda module integreert uw Outlook kalender direct in het CRM systeem. Bekijk afspraken, synchroniseer meetings met projecten en leads, en plan follow-ups zonder tussen applicaties te wisselen. De naadloze Microsoft 365 integratie verhoogt productiviteit.

## Navigatie

Selecteer **Agenda** in het zijmenu (kalender icoon). Eerste keer: autorisatie vereist via Microsoft login.

## Functies Overzicht

- **Outlook Synchronisatie**: Two-way sync met uw Outlook kalender
- **CRM Context**: Koppel meetings aan leads, projecten of contacten
- **Maand/Week/Dag Weergave**: Flexibele kalender views
- **Nieuwe Afspraak**: Creëer meetings direct vanuit CRM
- **Herinneringen**: Automatische reminders voor aankomende afspraken
- **Deelnemers**: Voeg collega's en klanten toe aan meetings

## Stap-voor-stap Instructies

1. **Outlook Koppelen**: Agenda openen → klik "Koppel Outlook" → Microsoft login → autoriseer toegang
2. **Kalender Bekijken**: Kalender toont automatisch Outlook afspraken na koppeling
3. **View Wijzigen**: Klik "Maand" / "Week" / "Dag" voor gewenste weergave
4. **Nieuwe Afspraak**: Klik "+" of dubbelklik op datum → vul titel, tijd, deelnemers → optioneel: koppel aan CRM record → opslaan
5. **CRM Koppeling**: Bij afspraak aanmaken → dropdown "Gerelateerd aan" → selecteer lead/project/contact → afspraak verschijnt in record timeline
6. **Afspraak Bewerken**: Klik op bestaande afspraak → wijzig details → opslaan (sync naar Outlook)
7. **Verwijderen**: Open afspraak → "Verwijderen" → bevestig (verwijdert uit Outlook + CRM)

## 💡 Tips & Best Practices

- Koppel alle klant meetings aan leads/projecten voor volledige historiek
- Gebruik recurring meetings voor vaste overleggen (week/maand)
- Voeg altijd notities toe aan afspraken voor post-meeting context
- Synchroniseer dagelijks: check kalender bij start van de dag
- Gebruik kleurcodering per type afspraak (intern vs extern)
- Stel 15min reminders in voor belangrijke client calls
- Check sync status regelmatig: rode icoon = sync error, groen = OK

## Veelgestelde Vragen

**Q: Zie ik ook privé afspraken vanuit Outlook?**
A: Alleen zakelijke afspraken van uw werk kalender worden gesynchroniseerd.

**Q: Wat gebeurt er als ik afspraak in Outlook wijzig?**
A: Wijziging synct automatisch naar CRM binnen enkele minuten.

**Q: Kunnen collega's mijn CRM gekoppelde afspraken zien?**
A: Alleen als zij deelnemer zijn aan de meeting of toegang hebben tot gekoppelde CRM record.`,

        'Contacten': `## Introductie

Contacten zijn de individuele personen binnen organisaties. Deze module beheert persoonlijke gegevens, communicatie historie en GDPR compliance. Een goed onderhouden contactendatabase is essentieel voor effectieve klantrelaties en naleving van privacywetgeving.

## Navigatie

Klik **Contacten** in zijmenu (user circle icoon). De pagina toont lijst van alle contactpersonen met zoek en filter mogelijkheden.

## Functies Overzicht

- **Persoonlijke Gegevens**: Naam, functie, email, telefoon, adres
- **Organisatie Koppeling**: Elk contact hoort bij één organisatie
- **Communicatie Log**: Historie van emails, calls en meetings
- **GDPR Compliance**: Toestemming tracking en privacy settings
- **Custom Fields**: Extra velden voor specifieke info (bijv. verjaardag)
- **Duplicaat Detectie**: Waarschuwing bij mogelijk dubbele contacten

## Stap-voor-stap Instructies

1. **Contact Aanmaken**: Klik "+ Nieuw Contact" → vul minimaal voornaam, achternaam, email → selecteer organisatie → opslaan
2. **GDPR Toestemming**: Bij aanmaken → vink "GDPR toestemming" aan als contact akkoord met dataverwerking
3. **Contact Bewerken**: Klik op contact in lijst → wijzig velden → opslaan
4. **Email Verzenden**: Open contact → klik email adres → Outlook opent met pre-filled to: veld
5. **Notitie Toevoegen**: Contact detail → tab "Notities" → typ communicatie summary → opslaan
6. **Duplicaat Check**: Bij aanmaken met bestaande email → systeem waarschuwt → bevestig of annuleer

## 💡 Tips & Best Practices

- Vraag altijd GDPR toestemming bij eerste contact (telefonisch of email)
- Update functie/rol wanneer contact promoveert of van baan wisselt
- Voeg mobiel nummer toe voor betere bereikbaarheid
- Link contact direct aan organisatie: lone contacts zijn minder nuttig
- Gebruik notities voor belangrijke voorkeuren (bijv. "belt liever niet voor 10:00")
- Check duplicaten maandelijks en merge waar nodig
- Tag contacten voor segmentatie (bijv. "decision maker", "technical contact")

## Veelgestelde Vragen

**Q: Wat als contact van organisatie wisselt?**
A: Maak nieuw contact bij nieuwe organisatie. Oude contact kan worden gearchiveerd maar bewaar historie.

**Q: Hoe verwijder ik contact volgens GDPR?**
A: Via "Verwijderen" button → systeem anonimiseert data ipv harde delete (compliance).

**Q: Kunnen meerdere gebruikers zelfde contact gebruiken?**
A: Ja, contacten zijn organisatie-breed toegankelijk (binnen tenant).`,

        'Offertes': `## Introductie

De Offertes module stelt u in staat professionele, merkgebonden offertes te creëren via een visuele PDF designer. Met placeholder systeem, versie beheer en automatische generatie bespaart u uren werk bij offerteproductie. Templates zorgen voor consistentie en kwaliteit.

## Navigatie

Ga naar **Offertes** in zijmenu (document icoon). Voor template beheer: **Instellingen** → **Offerte Templates**.

## Functies Overzicht

- **PDF Template Designer**: Visuele editor met drag-and-drop placeholders
- **Placeholder Systeem**: Dynamische velden (klant naam, project details, datum)
- **Versie Beheer**: Meerdere offerteversies per project met historiek
- **Automatische Nummering**: OFF-YYYY-XXX format (bijv. OFF-2024-001)
- **Lijn Items**: Product/dienst regels met prijs, BTW en totalen
- **PDF Generatie**: Instant download van professionele offerte PDF
- **Goedkeuring Flow**: Status tracking (concept, verzonden, geaccepteerd, afgewezen)

## Stap-voor-stap Instructies

**Template Aanmaken:**
1. Ga naar Instellingen → Offerte Templates → "+ Nieuwe Template"
2. Upload basis PDF (bijv. bedrijfs briefpapier met header/footer)
3. Open PDF Designer → sleep placeholders op template (klik placeholder → drag naar positie)
4. Configureer placeholder properties (font size, kleur, alignment)
5. Test met preview → opslaan als template

**Offerte Genereren:**
1. Offertes pagina → "+ Nieuwe Offerte" → selecteer project en template
2. Vul klant gegevens, project info en geldigheid in
3. Tab "Lijn Items" → voeg producten/diensten toe met prijs en BTW
4. Preview → controleer opmaak en data → "Genereer PDF"
5. Download offerte → deel met klant via email
6. Update status naar "Verzonden" na verzending

**Versie Beheer:**
1. Open bestaande offerte → "Nieuwe Versie" knop
2. Wijzig lijn items of prijzen → opslaan als v2, v3, etc.
3. Oude versies blijven beschikbaar in versie lijst

## 💡 Tips & Best Practices

- Maak aparte templates per dienst type (batterijen vs laadinfra)
- Gebruik placeholders voor alle variabele data (geen hardcoded klant namen)
- Test templates grondig met dummy data voor deployment
- Versie beheer: toon alleen laatste versie aan klant, bewaar oude intern
- Automatische nummering is uniek: gebruik dit in klant communicatie
- Voeg disclaimer/voorwaarden toe in template footer
- Gebruik grid snapping in designer voor professionele uitlijning (10px grid)

## Veelgestelde Vragen

**Q: Kan ik logo in offerte template aanpassen?**
A: Ja, upload nieuwe basis PDF met gewenst logo en maak nieuwe template.

**Q: Wat gebeurt er als klant offerte accepteert?**
A: Wijzig status naar "Geaccepteerd" → optioneel: converteer naar project voor realisatie fase.

**Q: Kan ik meerdere BTW tarieven gebruiken?**
A: Ja, elk lijn item heeft eigen BTW percentage (0%, 9%, 21%).`,

        'Businesscases': `## Introductie

Businesscases module ondersteunt financiële besluitvorming door ROI berekeningen, cashflow projecties en scenario vergelijkingen. Of het nu gaat om batterij investeringen of laadinfrastructuur - deze tool berekent rendement, terugverdientijd en subsidie mogelijkheden geautomatiseerd.

## Navigatie

Selecteer **Businesscases** in zijmenu (calculator icoon). Voor project-specifieke businesscases: open project → tab "Businesscase".

## Functies Overzicht

- **ROI Calculatie**: Automatische return on investment berekening
- **Cashflow Projecties**: Jaar-tot-jaar overzicht van kosten en opbrengsten
- **Scenario Vergelijking**: Vergelijk best case vs worst case vs realistic
- **Subsidie Integratie**: EIA, SDE++ en andere regelingen
- **Payback Timeline**: Visualisatie van terugverdientijd
- **Waterfall Charts**: Grafische weergave van kosten breakdown
- **Zympler Import**: Importeer energiedata voor realistische berekeningen
- **Excel Export**: Download volledige analyse voor externe rapportage

## Stap-voor-stap Instructies

**Nieuwe Businesscase:**
1. Klik "+ Nieuwe Businesscase" → geef naam → koppel aan project
2. Tab "Kosten" → vul in: batterij aanschaf, installatie, software setup, abonnement
3. Tab "Subsidies" → selecteer EIA en/of Flex-E → vul bedragen in
4. Tab "Opbrengsten" → importeer Zympler data of vul handmatig energiebesparingen in
5. Dashboard tab → bekijk ROI, payback period en cashflow grafieken

**Scenario's Vergelijken:**
1. Maak 3 businesscases voor zelfde project: "Conservative", "Realistic", "Optimistic"
2. Wijzig aannames per scenario (energieprijzen, gebruik, subsidies)
3. Gebruik "Vergelijk" knop → side-by-side overzicht van alle KPI's
4. Export naar Excel voor presentatie aan stakeholders

**Zympler Import:**
1. Businesscase → tab "Zympler" → upload .xlsx bestand van Zympler
2. Systeem parset energiedata en berekent automatisch opbrengsten
3. Review geparsete data → wijzig indien nodig → "Toepassen"

## 💡 Tips & Best Practices

- Maak altijd 3 scenario's (best/realistic/worst) voor risk assessment
- Update businesscases bij wijziging van energieprijzen of subsidies
- Gebruik Zympler import voor data-driven analyses (vermijd aannames)
- Check subsidie bedragen jaarlijks: EIA/SDE regelingen wijzigen
- Voeg extra kosten toe (onderhoud, verzekering) voor realistische ROI
- Export naar Excel voor klant presentaties en board meetings
- Link businesscases aan projecten: historische data voor toekomstige cases

## Veelgestelde Vragen

**Q: Hoe bereken ik ROI zonder Zympler data?**
A: Vul handmatig geschatte jaarlijkse besparingen in tab "Opbrengsten". Systeem berekent ROI op basis van input.

**Q: Kan ik businesscase dupliceren?**
A: Ja, open businesscase → "Dupliceren" → wijzig naam → aparte copy voor scenario's.

**Q: Wat is payback period?**
A: Aantal jaren tot investering is terugverdiend via besparingen. Ideaal <5 jaar voor batterijen.`
      },
      4: {
        'Flex-E': `## Introductie

Flex-E (Flexibility Energy) module simuleert energiebeheer scenario's om optimale batterij dimensionering te bepalen. Upload historische energiedata, bouw machine bibliotheken, en test verschillende batterij configuraties om piekvraag te reduceren en kosten te besparen. De simulatie engine voorspelt of een scenario binnen netcapaciteit blijft.

## Navigatie

Ga naar **Flex-E** in zijmenu (bliksem icoon). Voor project-gebonden simulaties: open project → tab "Flex-E".

## Functies Overzicht

- **Machine Library**: Database van machines met specs (kW, inrush, duty cycle)
- **Scenario Builder**: Combineer machines in operationele scenario's
- **Excel Upload**: Import kwartuurdata van energieleverancier
- **Simulatie Engine**: Berekent batterij performance per scenario
- **Succes Rate**: Percentage dagen dat batterij capacity volstaat
- **Deficit Analyse**: Visualisatie van momenten met tekort
- **Dag Detail**: Drill-down naar specifieke dagen met problemen
- **Batterij Dimensionering**: Bepaal benodigde kWh en kW op basis van simulaties

## Stap-voor-stap Instructies

**Machine Toevoegen:**
1. Flex-E → tab "Machines" → "+ Nieuwe Machine"
2. Vul specs in: naam, kW vermogen, voltage, phases, inrush (kW piek bij opstarten)
3. Optioneel: duty cycle (% actieve tijd), concurrency factor
4. Opslaan → machine beschikbaar voor scenario's

**Scenario Bouwen:**
1. Tab "Scenario's" → "+ Nieuw Scenario" → geef naam
2. Selecteer machines uit library → voeg toe aan scenario
3. Configureer schedule: welke machines wanneer actief (uren per dag)
4. Opslaan → scenario klaar voor simulatie

**Simulatie Uitvoeren:**
1. Tab "Simulaties" → "+ Nieuwe Simulatie" → selecteer scenario
2. Upload energiedata (Excel met kwartuurwaarden) via file picker
3. Configureer batterij: kWh capacity, kW vermogen, C-value
4. Stel netcapaciteit in (max kW beschikbaar)
5. Klik "Start Simulatie" → wacht op resultaten (enkele seconden)

**Resultaten Analyseren:**
1. Simulatie detail → check "Succes Rate" (ideaal >95%)
2. Bekijk "Worst Day" → dag met grootste tekort
3. Gebruik deficit grafiek om probleem tijdstippen te identificeren
4. Indien lage succes rate: verhoog batterij capacity of wijzig scenario

## 💡 Tips & Best Practices

- Bouw uitgebreide machine library: herbruikbaar voor alle projecten
- Test meerdere batterij groottes (50kWh, 100kWh, 200kWh) voor kosten-baten
- Gebruik 1 jaar energiedata voor betrouwbare simulaties (minimaal 3 maanden)
- Let op inrush kW: sommige machines hebben 3-5x opstartpiek
- Concurrency factor <1.0 als machines niet gelijktijdig draaien
- Vergelijk baseline (zonder batterij) vs scenario (met batterij) voor savings
- Export simulatie rapporten naar PDF voor klant presentaties

## Veelgestelde Vragen

**Q: Wat is C-value van batterij?**
A: Verhouding kW / kWh. Voorbeeld: 100kW / 200kWh = 0.5C (langzame lading/ontlading).

**Q: Waarom is succes rate niet 100%?**
A: Batterij capacity volstaat niet op pieken dagen. Vergroot kWh of reduceer piekvraag.

**Q: Kan ik meerdere scenario's tegelijk simuleren?**
A: Nee, maar u kunt ze snel na elkaar runnen en resultaten vergelijken in overzicht.`,

        'Battery Planner': `## Introductie

Battery Planner beheert de volledige batterij supply chain: van container tracking en voorraad beheer tot allocaties aan projecten en klanten. Met reserveringssysteem, 3D container visualisatie en status tracking behoudt u overzicht over tientallen batterijen in verschillende fases.

## Navigatie

Selecteer **Battery Planner** in zijmenu (batterij icoon). De pagina toont containers met daarin batterijen.

## Functies Overzicht

- **Container Management**: Track shipping containers met batterijen
- **3D Visualisatie**: Visuele weergave van batterijen in container
- **Voorraad Categorieën**: Stock (beschikbaar), To Order (besteld), In Transit (onderweg)
- **Allocaties**: Wijs batterijen toe aan leads/organisaties
- **Reserveringen**: Reserve toekomstige batterijen voor projecten
- **Status Tracking**: Available, Reserved, Allocated, Installed
- **Serienummer Beheer**: Unieke identificatie per batterij
- **Type Management**: Verschillende batterij types (50kWh, 100kWh, etc.)

## Stap-voor-stap Instructies

**Container Toevoegen:**
1. Battery Planner → "+ Nieuwe Container" → vul naam en arrival date in
2. Selecteer capacity (max aantal batterijen) → opslaan
3. Container verschijnt in lijst met lege slots

**Batterij Toevoegen:**
1. Open container → "+ Batterij Toevoegen" → selecteer type (50kWh/100kWh)
2. Vul specs: kWh capacity, kW vermogen, serienummer (optioneel)
3. Status = "Stock" (beschikbaar) → opslaan
4. Batterij verschijnt in 3D visualisatie binnen container

**Batterij Alloceren:**
1. Zoek batterij in voorraad → klik "Alloceren"
2. Selecteer lead of organisatie → vul notities in → bevestig
3. Status wijzigt naar "Allocated" → batterij gereserveerd voor klant
4. Bij installatie: update status naar "Installed" + vul installatie datum

**Reservering Aanmaken:**
1. Tab "Reserveringen" → "+ Nieuwe Reservering"
2. Selecteer batterij type, aantal, lead/organisatie → gewenste leverdatum
3. Opslaan → reservering zichtbaar in overzicht
4. Bij arrival van batterijen: converteer reservering naar allocatie

**To Order Beheren:**
1. Tab "To Order" → "+ Batterij Bestellen" → aantal en type
2. Vul verwachte leveringsdatum → opslaan
3. Bij ontvangst: verplaats naar container (wordt "Stock")

## 💡 Tips & Best Practices

- Update container arrival dates bij vertraging: overzicht blijft accuraat
- Gebruik serienummers voor warranty tracking en onderhoud historie
- Reserveer batterijen direct bij sales commitment (voorkom dubbele toewijzing)
- Monitor "To Order" wekelijks: voorkom voorraad tekorten
- 3D visualisatie is handig voor capacity planning per container
- Archiveer oude containers maar behoud data voor historiek
- Koppel allocaties aan CRM leads voor end-to-end tracking

## Veelgestelde Vragen

**Q: Wat is verschil tussen Allocated en Reserved?**
A: Reserved = toekomstige toezegging zonder fysieke batterij. Allocated = fysieke batterij toegewezen aan klant.

**Q: Kan ik batterij tussen containers verplaatsen?**
A: Ja, wijzig container ID in batterij record. Batterij verhuist naar nieuwe container.

**Q: Hoe track ik geïnstalleerde batterijen?**
A: Status "Installed" + vul installatie datum en locatie in notities veld.`,

        'To-do\'s': `## Introductie

De To-do's module is het centrale taakbeheer systeem voor CRM en Charging workflows. Creëer persoonlijke taken, team taken (gedeeld), en koppel taken aan projecten of locaties. Met afhankelijkheden, deadlines en prioriteiten behoudt u overzicht over alle werkzaamheden.

## Navigatie

Klik **To-do's** in zijmenu (checkbox icoon). De pagina toont al uw toegewezen taken.

## Functies Overzicht

- **Persoonlijke Taken**: Taken alleen voor uzelf
- **Team Taken**: Gedeelde taken zichtbaar voor hele team
- **Project Koppeling**: Taken gekoppeld aan CRM projecten
- **Locatie Koppeling**: Taken gekoppeld aan Charging locaties
- **Afhankelijkheden**: Taken die andere taken blokkeren
- **Status Tracking**: To Do, In Progress, Done, Blocked
- **Prioriteit**: Low, Medium, High, Urgent
- **Deadline Beheer**: Due dates met visuele indicators voor overdue

## Stap-voor-stap Instructies

**Persoonlijke Taak:**
1. To-do's pagina → "+ Nieuwe Taak" → vul titel en beschrijving
2. Wijs aan uzelf toe → stel deadline in → selecteer prioriteit
3. Laat "Project" en "Locatie" leeg → vink "Gedeeld" uit → opslaan

**Team Taak:**
1. "+ Nieuwe Taak" → vul details → wijs toe aan collega
2. Vink "Gedeeld met team" aan → opslaan
3. Taak zichtbaar voor alle CRM gebruikers (niet Charging users)

**Project Taak:**
1. Project detail → tab "Taken" → "+ Nieuwe Taak"
2. Taak automatisch gekoppeld aan project → vul details → opslaan
3. Taak verschijnt in To-do lijst + project overzicht

**Locatie Taak (Charging):**
1. Locatie detail → tab "To-do's" → "+ Nieuwe Taak"
2. Taak gekoppeld aan locatie → toewijzen aan Greenvision medewerker
3. Charging users zien alleen hun eigen locatie taken

**Afhankelijkheid Instellen:**
1. Open taak A → veld "Afhankelijk van" → selecteer taak B
2. Taak A kan pas starten wanneer B = "Done"
3. Visuele indicator toont blokkering

## 💡 Tips & Best Practices

- Gebruik prioriteit "Urgent" spaarzaam (alleen echte noodgevallen)
- Stel realistische deadlines: liever 1 dag extra dan te krap
- Update status dagelijks: team ziet voortgang
- Gebruik afhankelijkheden voor sequentiële workflows
- Team taken voor cross-functionele projecten (zichtbaarheid++)
- Filter op "Mijn taken" voor dagelijkse focus lijst
- Review overdue tasks wekelijks: reschedule of delegeer

## Veelgestelde Vragen

**Q: Waarom zie ik sommige project taken niet?**
A: U ziet alleen taken waar u eigenaar van bent of aan toegewezen bent, plus team taken.

**Q: Kunnen Charging users CRM project taken zien?**
A: Nee, Charging users hebben GEEN toegang tot project taken, alleen locatie taken.

**Q: Wat gebeurt er als afhankelijke taak wordt verwijderd?**
A: Afhankelijkheid link wordt verbroken, blokkerende taak wordt unblocked.`,

        'Instellingen': `## Introductie

Instellingen is het controle centrum voor persoonlijke voorkeuren en systeem configuratie. Hier koppelt u externe diensten (Outlook, SharePoint), beheert u uw profiel, en configureren super admins module toegang voor gebruikers.

## Navigatie

Klik **Instellingen** in zijmenu (tandwiel icoon). De pagina toont verschillende settings categorieën in cards.

## Functies Overzicht

- **Profiel Instellingen**: Naam, email, avatar, wachtwoord
- **Outlook Integratie**: Koppel Microsoft 365 kalender
- **SharePoint Integratie**: Verbind met SharePoint voor documentopslag
- **Module Beheer** (Super Admin): Enable/disable modules per gebruikersgroep
- **Notificatie Voorkeuren**: Email alerts, browser push notifications
- **Taal & Locatie**: Interface taal en tijdzone
- **Theme**: Dark mode / Light mode toggle

## Stap-voor-stap Instructies

**Profiel Bijwerken:**
1. Instellingen → card "Profiel" → klik "Bewerken"
2. Wijzig display name, upload avatar → opslaan
3. Voor wachtwoord wijziging: aparte "Wijzig Wachtwoord" knop

**Outlook Koppelen:**
1. Card "Outlook Integratie" → klik "Verbind Outlook"
2. Microsoft login scherm → autoriseer toegang → bevestig
3. Groene status indicator = gekoppeld
4. Gebruik "Ontkoppel" om link te verbreken

**SharePoint Koppelen:**
1. Card "SharePoint" → klik "Verbind SharePoint"
2. Autoriseer toegang → selecteer site en drive
3. Systeem maakt automatisch folder structuur voor organisaties/projecten

**Module Beheer (Super Admin):**
1. Card "Module Beheer" → lijst van alle modules
2. Toggle modules aan/uit per gebruikersrol
3. Disabled modules verdwijnen uit menu voor die rol
4. Wijzigingen direct actief (geen herstart vereist)

**Theme Wijzigen:**
1. Card "Uiterlijk" → selecteer Light / Dark mode
2. Instant switch, voorkeur wordt opgeslagen

## 💡 Tips & Best Practices

- Koppel Outlook en SharePoint direct bij onboarding (1x setup)
- Upload professionele avatar voor herkenning door collega's
- Super admins: disable modules die niet gebruikt worden (vermindert UI clutter)
- Test SharePoint koppeling met test organisatie voor deployment
- Update email notificatie voorkeuren bij overload inbox
- Dark mode is vriendelijker voor ogen bij lang schermwerk
- Check integratie status maandelijks (tokens kunnen expiren)

## Veelgestelde Vragen

**Q: Kan ik Outlook koppelen zonder admin rechten?**
A: Ja, elk individu koppelt eigen Outlook account. Geen admin vereist.

**Q: Wat gebeurt er als ik SharePoint ontkoppel?**
A: Bestaande bestanden blijven in SharePoint, maar nieuwe uploads gaan niet meer naar SharePoint.

**Q: Kunnen normale users modules enable/disable?**
A: Nee, alleen super_admin rol heeft toegang tot module beheer.`
      },
      5: {
        'Realisatie': `## Introductie

De Realisatie module beheert laadplein projecten in de bouwfase. Van voorlopig ontwerp tot definitieve oplevering volgt u de constructie door 4 stages. Deze module overbrugt de gap tussen contract (Acquisitie) en operatie, met focus op planning, uitvoering en kwaliteitscontrole.

## Navigatie

Klik **Charging** → **Realisatie** in zijmenu (hamer icoon). De pagina toont 4 kanban kolommen voor bouwfases.

## Functies Overzicht

- **4 Bouw Stages**:
  1. **Voorlopig Ontwerp**: Schetsmatige plannen
  2. **Definitief Ontwerp**: Finale technische tekeningen
  3. **Bouwvoorbereiding**: Vergunningen, inkoop, logistiek
  4. **Bouwuitvoering**: Fysieke constructie
- **Drag-and-Drop**: Verplaats locaties tussen stages
- **Document Management**: CAD tekeningen, vergunningen, build photos
- **Milestone Tracking**: Key dates (vergunning, start bouw, oplevering)
- **Inspectie Koppeling**: Link site inspections aan locaties
- **Contractor Management**: Track externe aannemers en leveranciers

## Stap-voor-stap Instructies

1. **Locatie naar Realisatie**: Vanuit Acquisitie → stage wijzigen naar "Voorlopig Ontwerp" → locatie verschijnt in Realisatie module
2. **Stage Progressie**: Sleep locatie van Voorlopig → Definitief Ontwerp → Bouwvoorbereiding → Bouwuitvoering
3. **Documenten Uploaden**: Open locatie → tab "Documenten" → upload tekeningen/vergunningen → categoriseer
4. **Milestone Instellen**: Tab "Projectmanagement" → "+ Milestone" → "Vergunning ingediend" + datum → opslaan
5. **Inspectie Plannen**: Tab "Inspecties" → wijs inspector toe → set datum → inspector krijgt notificatie
6. **Oplevering**: Bouwuitvoering voltooid → verplaats naar "Operatie" stage

## 💡 Tips & Best Practices

- Plan inspecties bij elke stage overgang (kwaliteitscheck)
- Upload wekelijks bouw foto's voor visuele voortgang tracking
- Stel milestones in voor kritische paden (vergunning deadlines)
- Link aannemers aan notities voor duidelijke communicatie historie
- Gebruik tag systeem voor project type (bijv. "snellader", "DC", "AC")
- Monitor bouwvoorbereiding nauw: vertragingen hier vertragen hele project
- Documenteer afwijkingen van ontwerp in notities voor toekomstige learnings

## Veelgestelde Vragen

**Q: Wanneer verplaats ik van Acquisitie naar Realisatie?**
A: Na getekend contract en GO voor ontwerp fase.

**Q: Kan ik terug naar Acquisitie als project stopt?**
A: Ja, verplaats naar "Verloren" of terug naar eerdere Acquisitie stage indien heronderhandeling.

**Q: Hoe track ik meerdere aannemers?**
A: Gebruik notities tab met @mentions of tag systeem per contractor.`,

        'Operatie': `## Introductie

Operatie module beheert live, operationele laadpleinen. Na oplevering verschijnen locaties hier voor onderhoud, performance monitoring en incident management. Deze module is cruciaal voor lange-termijn beheer van uw laadinfrastructuur netwerk.

## Navigatie

Selecteer **Charging** → **Operatie** (activity icoon). Toont alle actieve laadpleinen.

## Functies Overzicht

- **Live Locaties**: Operationele laadpleinen na oplevering
- **Onderhoud Tracking**: Preventief en correctief onderhoud logging
- **Performance Metrics**: Uptime, energie doorvoer, gebruiksstatistieken
- **Incident Management**: Storing registratie en oplossing tracking
- **Inspectie Historie**: Alle uitgevoerde inspecties
- **Contract Beheer**: SLA's en service contracten per locatie
- **Revenue Tracking**: Omzet per locatie (indien applicabel)

## Stap-voor-stap Instructies

1. **Locatie Ontvangen**: Vanuit Realisatie → stage "Bouwuitvoering" afgerond → verplaats naar "Operatie" stage
2. **Onderhoud Loggen**: Open locatie → tab "Onderhoud" → "+ Log Entry" → preventief/correctief → beschrijving → opslaan
3. **Incident Rapporteren**: Tab "Incidenten" → "+ Nieuw Incident" → severity, beschrijving, affected chargers → assign technician
4. **Performance Bekijken**: Tab "Statistieken" → uptime %, kWh doorvoer, aantal laadsessies
5. **Inspectie Inplannen**: Jaarlijkse inspectie → tab "Inspecties" → schedule inspector → set reminder
6. **Contract Checken**: Tab "Contracten" → upload SLA documenten → set expiry reminders

## 💡 Tips & Best Practices

- Plan preventief onderhoud ieder kwartaal (voorkom storingen)
- Log alle incidenten, ook kleine: trend analyse voorkomt grotere problemen
- Monitor uptime % per locatie: <95% uptime vereist actie
- Gebruik incident severity correct (Critical/High/Medium/Low) voor prioritering
- Koppel revenue data aan locaties voor ROI analyse
- Set automatische reminders voor contract verlengingen (3 maanden vooraf)
- Review operationele data maandelijks met team voor continue verbetering

## Veelgestelde Vragen

**Q: Wat is verschil tussen onderhoud en incident?**
A: Onderhoud = gepland werk. Incident = ongeplande storing of probleem.

**Q: Hoe lang bewaar ik locaties in Operatie?**
A: Permanent, tenzij locatie wordt verkocht of buiten gebruik gesteld. Dan archiveren.

**Q: Kunnen klanten performance data zien?**
A: Hangt af van configuratie. Mogelijk via klant portal (toekomstige functionaliteit).`,

        'Archief': `## Introductie

Archief (Verloren) module bewaard locaties die niet doorgegaan zijn. In plaats van deleten, archiveert u locaties met reden en lessons learned. Deze historische data is waardevol voor toekomstige deal analyses en market intelligence.

## Navigatie

Ga naar **Charging** → **Archief** (archive icoon). Toont alle verloren/gearchiveerde locaties.

## Functies Overzicht

- **Soft Delete**: Locaties niet verwijderd, alleen gearchiveerd
- **Loss Reason Tracking**: Waarom verloren (prijs, timing, concurrent)
- **Lessons Learned**: Documenteer leerpunten voor future deals
- **Competitor Intelligence**: Wie won de deal? Welke prijs?
- **Restore Functie**: Heractiveer locatie indien situatie wijzigt
- **Search & Filter**: Zoek archived locaties op stad, reden, datum
- **Reporting**: Analyse van verlies redenen voor strategic insights

## Stap-voor-stap Instructies

1. **Locatie Archiveren**: Vanuit Acquisitie/Realisatie → stage wijzigen naar "Verloren" → locatie verplaatst naar Archief
2. **Loss Reason Toevoegen**: Open gearchiveerde locatie → veld "Loss Reason" → selecteer categorie (prijs/timing/concurrent) → opslaan
3. **Lessons Learned**: Tab "Notities" → documenteer wat geleerd is → tag als "lesson learned" voor toekomstige reference
4. **Concurrent Info**: Als concurrent won: noteer naam concurrent, geschatte prijs, USP's
5. **Restore Locatie**: Indien situatie wijzigt → wijzig stage terug naar Acquisitie → locatie wordt heractiveerd
6. **Analyse Verliezen**: Filter op loss reason → aggregeer data → identificeer patronen

## 💡 Tips & Best Practices

- Archiveer altijd met loss reason (geen "unknown" categorie gebruiken)
- Documenteer concurrent informatie: intel voor future competitive situations
- Tag locaties met klant segment voor segment-specifieke loss analysis
- Review archief kwartaal: zijn er locaties om te reactiveren? (situatie change)
- Gebruik lessons learned bij pre-sales voor nieuwe deals (avoid repeat mistakes)
- Analyseer loss reasons jaarlijks: zijn er trends? Prijs altijd de reden?
- Bewaar contact details: klant kan terugkomen na 6-12 maanden

## Veelgestelde Vragen

**Q: Kan ik gearchiveerde locatie permanent deleten?**
A: Technisch mogelijk maar niet aangeraden. Historische data is waardevol. Soft delete voldoet.

**Q: Tellen gearchiveerde locaties mee in KPI's?**
A: Nee, alleen actieve locaties (Acquisitie/Realisatie/Operatie) tellen in dashboards.

**Q: Hoe vaak komen gearchiveerde deals terug?**
A: Ongeveer 10-15% binnen 12 maanden. Vandaar restore functionaliteit.`,

        'Concurrentie': `## Introductie

Concurrentie module tracked laadpleinen van concurrenten voor market intelligence. Visualiseer waar LEAP24, Milence en andere spelers actief zijn, en analyseer uw marktpositionering. Met automatische data import en kaart overlay krijgt u strategic insights voor nieuwe acquisities.

## Navigatie

Klik **Charging** → **Concurrentie** (swords icoon). Pagina toont kaart met concurrent sites.

## Functies Overzicht

- **Competitor Organisaties**: Bedrijven met accent kleur en logo
- **Competitor Sites**: Individuele laadplein locaties per organisatie
- **Automatische Import**: LEAP24 en Milence data via edge function
- **Kaart Overlay**: Rode markers voor concurrent sites
- **Organisatie Filter**: Klik logo om alleen die concurrent te tonen
- **Site Details**: Charger count, power capacity, operational status
- **Export Integratie**: Toon competitors op export maps voor presentaties

## Stap-voor-stap Instructies

**Concurrent Organisatie Toevoegen:**
1. Concurrentie → "+ Nieuwe Organisatie" → vul naam, website, accent kleur
2. Optioneel: upload logo → opslaan
3. Organisatie verschijnt in filter bar onder kaart

**Concurrent Site Toevoegen:**
1. "+ Nieuwe Site" → selecteer organisatie (verplicht) → vul adres/stad
2. Vul chargers count, power (kW), operational status → opslaan
3. Site verschijnt als rode marker op kaart

**Automatische Import:**
1. Concurrentie pagina → edge function detecteert <15 LEAP24 sites
2. Automatische batch import van 20+ sites → toast notificatie
3. Check kaart: nieuwe LEAP24 markers zichtbaar

**Filteren op Organisatie:**
1. Klik LEAP24 logo in filter bar → alleen LEAP24 sites visible
2. Klik nogmaals → toggle off, toon alle competitors again

**Export Overlay:**
1. Ga naar Export module → toggle "Concurrentie" aan
2. Competitors tonen als rode markers naast eigen locaties (blauw/oranje/groen)

## 💡 Tips & Best Practices

- Update concurrent sites maandelijks voor actuele market intelligence
- Gebruik accent kleuren per concurrent voor visuele differentiatie
- Noteer operational status: planned vs live sites (forecast vs current threat)
- Analyse overlap: waar zijn concurrenten EN jij actief? (competitive zones)
- Check competitor sites bij nieuwe acquisitie prospects (avoid oversaturated markets)
- Export kaarten met competitor overlay voor board presentaties
- Tag eigen locaties met "competitive" als direct naast concurrent (price pressure)

## Veelgestelde Vragen

**Q: Hoe vaak refresh LEAP24 data?**
A: Bij pagina load als <15 sites. Anders handmatige update via edge function trigger (ask admin).

**Q: Kan ik eigen concurrenten toevoegen (niet LEAP24/Milence)?**
A: Ja, via "+ Nieuwe Organisatie" voeg je elke concurrent toe.

**Q: Waarom rode markers voor competitors?**
A: Visuele differentiatie: rood=threat (concurrent), groen=opportunity (wensen), blauw/oranje=eigen pipeline.`,

        'Wensen': `## Introductie

Wensen module tracks gewenste laadplein locaties van transport bedrijven en klanten. In tegenstelling tot Concurrentie (threats), vertegenwoordigen wensen opportunities: plekken waar klanten laadinfra willen. Met groene markers op kaart visualiseert u klant vraag voor strategic planning.

## Navigatie

Selecteer **Charging** → **Wensen** (heart icoon). Pagina toont kaart met gewenste locaties.

## Functies Overzicht

- **Wish Organisaties**: Transporteurs/klanten met contact info
- **Wish Sites**: Gewenste locaties met priority en timeline
- **Priority Levels**: Low/Medium/High/Critical voor prioritering
- **Timeline Tracking**: Gewenste realisatie datum per locatie
- **Contact Details**: Direct contact voor sales follow-up
- **Kaart Overlay**: Groene markers voor wensen (vs rode competitors)
- **Capacity Wensen**: Desired power (kW) en charger count per site

## Stap-voor-stap Instructies

**Wens Organisatie Toevoegen:**
1. Wensen → "+ Nieuwe Organisatie" → vul transporteur naam, contact persoon, email/telefoon
2. Optioneel: website, notities → opslaan
3. Organisatie beschikbaar voor site koppeling

**Gewenste Site Toevoegen:**
1. "+ Nieuwe Wens Site" → selecteer organisatie → vul gewenste stad/adres
2. Priority (High voor hot leads) → desired timeline (bijv. Q2 2025)
3. Gewenste capacity: kW vermogen, aantal chargers → opslaan
4. Site verschijnt als groene marker op kaart

**Sales Follow-up:**
1. Open wens site → check contact details organisatie
2. Tab "Notities" → log sales call outcomes
3. Bij conversion naar deal → maak Acquisitie locatie → link wens in notities

**Priority Filtering:**
1. Filter dropdown → selecteer "High Priority" → alleen critical wensen visible
2. Focus sales efforts op high priority sites

**Export Overlay:**
1. Export module → toggle "Wensen" aan
2. Groene markers tonen klant vraag naast eigen pipeline en competitors

## 💡 Tips & Best Practices

- Update wensen direct na klant gesprekken (real-time demand tracking)
- Gebruik priority levels voor sales funnel management (focus high first)
- Check wensen vs bestaande pipeline: overlap = quick win mogelijkheid
- Tag wensen met klant segment (logistics, retail, public) voor analysis
- Cross-reference wensen met competitor sites: kan je sneller zijn?
- Use timeline field voor realistic planning en capacity forecasting
- Link gewonnen deals terug naar oorspronkelijke wens (conversion tracking)

## Veelgestelde Vragen

**Q: Wat is verschil tussen Wens en Acquisitie locatie?**
A: Wens = klant vraag zonder commitment. Acquisitie = actieve sales pipeline met prospect.

**Q: Wanneer converteer ik wens naar Acquisitie?**
A: Bij serieuze sales engagement (klant wil offerte, site bezoek gepland).

**Q: Kunnen wensen vervallen?**
A: Ja, update priority naar "Low" of delete als klant niet meer geïnteresseerd. Bewaar voor historische demand data.`,

        'To-do\'s': `## Introductie

To-do's in Charging module zijn locatie-gebonden taken voor Charging gebruikers. In tegenstelling tot CRM to-do's (project-based), focussen deze op specifieke laadpleinen. Met deadline tracking en toewijzing aan Greenvision medewerkers blijft operationeel werk georganiseerd.

## Navigatie

**Charging Portal**: Klik **To-do's** in zijmenu (checkbox icoon)
**Locatie Detail**: Open locatie → tab "Projectmanagement" → sectie "To-do's"

## Functies Overzicht

- **Locatie Scope**: Taken gekoppeld aan specifieke laadpleinen
- **Restricted Access**: Charging users zien ALLEEN eigen toegewezen taken
- **CRM Visibility**: CRM admins zien alle locatie to-do's
- **Assignee Options**: Toewijzen aan Greenvision employees (niet inspectors/klanten)
- **Status Tracking**: To Do, In Progress, Done
- **Priority Levels**: Low, Medium, High, Urgent
- **Deadline Alerts**: Visuele indicators voor overdue tasks

## Stap-voor-stap Instructies

**Taak Aanmaken (Locatie Context):**
1. Open locatie detail → tab "Projectmanagement" → "+ Nieuwe Taak"
2. Titel (bijv. "Inspectie inplannen"), beschrijving → opslaan
3. Taak automatisch gekoppeld aan locatie

**Taak Toewijzen:**
1. Open taak → dropdown "Toegewezen aan" → selecteer Greenvision medewerker
2. Set deadline → prioriteit → opslaan
3. Toegewezen persoon krijgt notificatie

**Status Updaten:**
1. Open taak → wijzig status naar "In Progress" bij start → "Done" bij voltooiing
2. Of: checkbox op taak lijst voor quick "Done" toggle

**Mijn Taken Bekijken:**
1. To-do's hoofdpagina (zijmenu) → toont ALLEEN aan u toegewezen taken
2. Filter op status/priority voor gefocuste werklijst
3. Sorteer op deadline voor urgency prioritering

**Locatie To-do's Overzicht (CRM Admin):**
1. Locatie detail → tab "Projectmanagement" → sectie To-do's
2. Toont ALLE taken voor die locatie (niet alleen eigen)
3. Handig voor coördinatie en oversight

## 💡 Tips & Best Practices

- Maak taken specifiek per locatie (niet generiek "check alle sites")
- Gebruik descriptieve titels: "OIW01 - Vergunning check" vs "Check ding"
- Set realistische deadlines: beter 1 dag ruimte dan overdue
- Priority "Urgent" = echte emergencies (storing, veiligheid issue)
- Update status dagelijks voor team visibility
- Link taken aan milestones in timeline voor context
- Review locatie to-do's wekelijks in teamoverleg

## Veelgestelde Vragen

**Q: Waarom zie ik geen CRM project taken?**
A: Charging users hebben GEEN toegang tot CRM project scope. Alleen locatie taken.

**Q: Kan ik taken toewijzen aan inspectors?**
A: Nee, alleen aan interne Greenvision medewerkers (admin/super_admin/CRM/Charging roles).

**Q: Zie ik taken van andere Charging users?**
A: Nee, alleen taken aan u toegewezen OF door u aangemaakt. CRM admins zien wel alles.`
      }
    };

    return batches[batchNumber] || {};
  };

  // Upgrade specific batch
  const upgradeBatchMutation = useMutation({
    mutationFn: async (batchNumber: number) => {
      if (!currentManual) throw new Error('No manual found');

      const batchContent = getBatchContent(batchNumber);
      const updates = Object.entries(batchContent);

      for (const [title, content] of updates) {
        const section = sections.find(s => s.title === title);
        if (section) {
          const { error } = await supabase
            .from('documentation_sections')
            .update({ content, updated_at: new Date().toISOString() })
            .eq('id', section.id);

          if (error) throw error;
        }
      }
    },
    onSuccess: (_, batchNumber) => {
      queryClient.invalidateQueries({ queryKey: ['documentation-sections'] });
      toast.success(`Batch ${batchNumber} content upgraded!`);
    },
    onError: (error, batchNumber) => {
      console.error(`Error upgrading batch ${batchNumber}:`, error);
      toast.error(`Fout bij upgraden batch ${batchNumber}`);
    },
  });

  return {
    currentManual,
    sections,
    allVersions,
    isLoading: manualsLoading || sectionsLoading,
    updateSection: updateSectionMutation.mutate,
    createVersion: createVersionMutation.mutate,
    publishVersion: publishVersionMutation.mutate,
    initializeManual: initializeManualMutation.mutate,
    upgradeBatch: upgradeBatchMutation.mutate,
    isUpdating: updateSectionMutation.isPending,
    isCreatingVersion: createVersionMutation.isPending,
    isPublishing: publishVersionMutation.isPending,
    isInitializing: initializeManualMutation.isPending,
    isUpgradingBatch: upgradeBatchMutation.isPending,
  };
}
